import './BundleAttach.sass';

import React, {
  memo,
  CSSProperties,
  FC,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import SortableList, { SortableItem } from 'react-easy-sort';

import { BundleAttachProps } from '$core/CreateBundle/BundleAttach/BundleAttach.interface';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '$store/rootReducer';
import { attachActions, attachItemsSelector, attachPhotosSelector } from '$store/attach';
import { goTo, showModal } from '$navigation/helper';
import { ActionBottomSheet, Button } from '$uikit';
import { cloudActions } from '$store/cloud';
import { imActions } from '$store/im';
import router from '$navigation/router';
import { CLOUD } from '$shared/constants/pages';
import { IcCloud28, IcGallery28, IcUpload28, IcVideo28 } from '$assets';
import { useDesktop, useTranslator } from '$hooks';
import { AttachActions } from '$core/Chat/SendForm/SendForm';
import { useRoute } from 'react-router5';
import { BundleAttachItem } from '$core/CreateBundle/BundleAttachItem/BundleAttachItem';
import { AttachPhoto, AttachType } from '$store/models';
import useSize from '@react-hook/size';
import { confirmSheet, classNames as cn, ATTACHMENTS_COUNT_LIMIT } from '$utils';
import { toastActions } from '$store/toast';

const BundleAttachComponent: FC<BundleAttachProps> = ({
  pickerId,
  limit = ATTACHMENTS_COUNT_LIMIT,
  decryptKey,
  showCoverLabel = false,
  disableTouch,
}) => {
  const mediaAttachments: AttachPhoto[] = useSelector((state: RootState) =>
    attachPhotosSelector(state, pickerId),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth] = useSize(containerRef);
  const [isActionShow, setActionsShown] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [photoInputKey, setPhotoInputKey] = useState(Date.now());
  const [videoInputKey, setVideoInputKey] = useState(-Date.now());
  const dispatch = useDispatch();
  const t = useTranslator();
  const { route } = useRoute();

  const uploadVideo = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const file = e.target.files?.[0];
      file &&
        dispatch(
          attachActions.uploadVideo({
            pickerId,
            limit,
            file: file || null,
            dispatch,
          }),
        );
      dispatch(attachActions.setIsAdditionalModalOpened(true));
      showModal('upload_video', false);
      setVideoInputKey(-Date.now());
    },
    [pickerId, limit],
  );

  const uploadPhoto = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      if (!e.target.files?.length) return;
      dispatch(
        attachActions.uploadPhotos({
          pickerId,
          limit,
          files: e.target.files,
          watermark: false,
        }),
      );
      setPhotoInputKey(Date.now());
    },
    [pickerId, limit],
  );

  const handleAction = (value: AttachActions) => {
    setActionsShown(false);

    switch (value) {
      case AttachActions.UPLOAD_PHOTO:
        photoInputRef.current?.click();
        break;

      case AttachActions.UPLOAD_VIDEO:
        videoInputRef.current?.click();
        break;

      case AttachActions.ATTACH_FROM_CLOUD:
        dispatch(
          cloudActions.setSavedContentActions([
            attachActions.pushPhotos({ pickerId, photos: mediaAttachments }),
          ]),
        );
        dispatch(
          cloudActions.setAttachParams({
            pickerId,
            routeName: route.name,
            routeParams: route.params,
          }),
        );
        goTo(
          CLOUD,
          {},
          {
            limit: limit - mediaCount,
          },
        );
    }
  };

  const actions = useMemo(() => {
    const items = [
      {
        label: t('upload_photo'),
        value: AttachActions.UPLOAD_PHOTO,
        icon: <IcGallery28 />,
      },
      {
        label: t('upload_video'),
        value: AttachActions.UPLOAD_VIDEO,
        icon: <IcVideo28 />,
      },
      {
        label: t('attach_from_cloud'),
        value: AttachActions.ATTACH_FROM_CLOUD,
        icon: <IcCloud28 />,
      },
    ];
    return items;
  }, [t]);

  const mediaCount = mediaAttachments.length;

  const onOrderChange = useCallback(
    (oldIndex, newIndex) => {
      dispatch(
        attachActions.swapPhotos({
          pickerId,
          oldIndex: oldIndex,
          newIndex: newIndex,
        }),
      );
    },
    [pickerId, mediaCount],
  );

  const handleUploadClick = useCallback(() => {
    if (mediaCount >= limit) {
      dispatch(toastActions.setToastFail(t('photos_limit_error_custom', { limit })));
      return;
    }
    setActionsShown(true);
  }, [mediaCount, limit]);

  const gap = 2;
  const columnCount = containerWidth < 400 ? 3 : 4;
  const rowCount = Math.ceil((mediaCount + 1) / columnCount);
  const itemSize = containerWidth / columnCount - gap;

  return (
    <div ref={containerRef} className="BundleAttach">
      <SortableList
        onSortEnd={onOrderChange}
        className="BundleAttach__grid"
        draggedItemClassName="BundleAttach__dragged"
      >
        {mediaAttachments.map((item, index) => (
          <SortableItem key={item.photoId + '_' + index}>
            <div>
              <BundleAttachItem
                decryptKey={decryptKey}
                onDelete={async () => {
                  if (await confirmSheet(t('bundles_form_attach_delete_confirm'))) {
                    dispatch(
                      attachActions.removePhoto({ pickerId, photoId: item.photoId }),
                    );
                  }
                }}
                showCoverLabel={showCoverLabel && !index}
                attach={{ type: AttachType.PHOTO, photo: item }}
              />
            </div>
          </SortableItem>
        ))}
        <div
          onClick={handleUploadClick}
          style={{ '--size': itemSize + 'px' } as CSSProperties}
          className="BundleAttach__uploadButton"
        >
          <div className="BundleAttach__uploadButton__content">
            <IcUpload28 /> Upload
          </div>
        </div>
      </SortableList>
      <div className="BundleAttach__inputs">
        <input
          type="file"
          accept="video/*"
          key={videoInputKey}
          ref={videoInputRef}
          onChange={uploadVideo}
          multiple
        />
        <input
          type="file"
          key={photoInputKey}
          accept="image/*"
          ref={photoInputRef}
          onChange={uploadPhoto}
          multiple
        />
      </div>
      <ActionBottomSheet
        title={t('message_actions_attach_title')}
        isOpened={isActionShow}
        options={actions}
        onChange={(action) => handleAction(action as AttachActions)}
        onClose={() => setActionsShown(false)}
        isAllGrouped={true}
      />
    </div>
  );
};

export const BundleAttach = memo(BundleAttachComponent);
